import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {RecipePart} from "../../../dtos/recipePart";
import {Recipe} from "../../../dtos/recipe";
import {first, tap} from "rxjs/operators";
import {AngularFireStorage} from "@angular/fire/compat/storage";
import {BehaviorSubject, Observable, of} from "rxjs";
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {AngularFireAuth} from "@angular/fire/compat/auth";
import {RecipeService} from "../../../recipe-serivce.service";
import {ComponentCanDeactivate} from "../../../guard/pending-changes.guard";
import firebase from "firebase/compat";
import firestore = firebase.firestore;

@Component({
  inputs: ['recipe'],
  selector: 'app-recipe-edit',
  templateUrl: './recipe-edit.component.html',
  styleUrls: ['./recipe-edit.component.css']
})
export class RecipeEditComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  @ViewChild('labelInputId', {static: false}) labelInput: ElementRef;
  public recipe: Recipe = null;
  private recipe$: Observable<Recipe>;
  private originalRecipe: Recipe = null;

  constructor(
    private route: ActivatedRoute,
    private storage: AngularFireStorage,
    private db: AngularFirestore,
    private auth: AngularFireAuth,
    private recipeService: RecipeService,
    private router: Router) {
  }

  ngOnInit() {
    this.route.params
      .pipe(
        first()
      )
      .subscribe(params => {
        if (!params || !params.hasOwnProperty('id')) {
          const recipe = new Recipe({});
          const part = new RecipePart({});
          part.steps.push(null);
          recipe.parts.push(part);
          this.recipe$ = new BehaviorSubject(recipe);
          this.recipe = recipe;
          this.originalRecipe = new Recipe(recipe);
        } else {
          this.recipe$ = this.recipeService.getRecipe(params['id']).pipe(
            first(),
            tap(recipe => {
              const newRecipe = new Recipe(recipe);
              newRecipe.parts.forEach(part => {
                part.steps.push(null);
              });
              this.recipe = newRecipe;
              this.originalRecipe = new Recipe(recipe);
            })
          );
          this.recipe$.subscribe();
        }
      });
  }

  ngOnDestroy(): void {
  }

  addLabel(label: string) {
    if (this.recipe.labels.indexOf(label) == -1 && label.trim().length > 0)
      this.recipe.labels.push(label);
    this.labelInput.nativeElement.value = '';
  }

  removeLabel(label: string) {
    const index = this.recipe.labels.indexOf(label);
    if (index != -1)
      this.recipe.labels.splice(index, 1);
  }

  addPart() {
    const recipePart = new RecipePart({});
    recipePart.steps.push(null);
    this.recipe.parts.push(recipePart);
  }

  removePart(part: number) {
    this.recipe.parts.splice(part, 1);
  }

  isNewRecipe(): boolean {
    return this.recipe.id == null;
  }

  onDelete() {
    if (window.confirm('Ben je zeker dat je dit recept wil verwijderen? Dit kan niet ongedaan gemaakt worden!')) {
      this.db.collection('recipes').doc(this.recipe.id).delete().catch(e => {
        console.error(`Could not delete document with id ${this.recipe.id}`, e);
        //this.notificationsService.error('Verwijderen mislukt', 'Probeer opnieuw. Indien het probleem aanhoudt, stuur dan een mailtje met details naar jdepypere@gmail.com').animate;
      }).then(() => {
        //this.notificationsService.success('Recept verwijderd').animate
        this.router.navigate(['recepten']);
      });
    }
  }

   async onSave() {
    const writeBatch: firestore.WriteBatch = this.db.firestore.batch();

    // write recipe
    var jsonRec = {
      "name": this.recipe.name,
      "description": this.recipe.description,
      "nrOfPeople": this.recipe.nrOfPeople,
      "path": this.recipe.path,
      "downloadURL": this.recipe.downloadURL,
      "totalDuration": this.recipe.getTotalNrOfMinutes(),
      "authorUid": (await this.auth.currentUser).uid,
      "labels": this.recipe.labels
    };

    const isCreation: boolean = this.recipe.id == null;

    if (this.isNewRecipe())
      this.recipe.id = this.db.createId();

    writeBatch.set(this.db.collection('recipes').doc(this.recipe.id).ref, jsonRec, {merge: true});

    var taskCollection = this.db.collection('recipes').doc(this.recipe.id).collection('tasks');

    // delete all original tasks
    for (let i = 0; i < this.originalRecipe.parts.length; i++) {
      writeBatch.delete(taskCollection.doc(i.toString()).ref);
    }

    // insert all tasks
    this.recipe.parts.forEach((part, index) => {
      var ingredients = [];
      part.ingredients.forEach(ingredient => {
        ingredients.push({
          'amount': ingredient.amount,
          'unit': ingredient.unit,
          'name': ingredient.name
        })
      });
      var steps = [];
      part.steps.forEach(step => {
        if (step && step.trim().length > 0)
          steps.push(step);
      });
      var jsonRecPart = {
        "duration": part.duration ? part.duration : 15,
        "name": part.name ? part.name : '',
        "description": part.description ? part.description : '',
        "steps": steps,
        "ingredients": ingredients
      };
      writeBatch.set(taskCollection.doc(index.toString()).ref, jsonRecPart);
    });
    writeBatch.commit().catch(reason => {
      console.error(reason);
      //this.notificationsService.error('Wijzigingen niet opgeslagen', "Er ging iets fout bij het opslaan van je recept. Probeer opnieuw. Indien het probleem aanhooudt, stuur dan een mailtje naar jdepypere@gmail.com met de details.").animate;
    }).then(() => {
      this.originalRecipe = this.recipe;
      //this.notificationsService.success(isCreation ? 'Recept aangemaakt' : 'Wijzigingen opgeslagen').animate;
      this.router.navigate(['recepten', this.recipe.id]);
    });
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.isChanged();
  }

  isChanged(): boolean {
    const rec = new Recipe(this.recipe);

    rec.parts.forEach(part => {
      var steps = [];
      part.steps.forEach(step => {
        if (step && step.trim().length > 0)
          steps.push(step);
      });
      part.steps = steps;
    });

    const originalRec = new Recipe(this.originalRecipe);
    originalRec.parts.forEach(part => {
      var steps = [];
      part.steps.forEach(step => {
        if (step && step.trim().length > 0)
          steps.push(step);
      });
      part.steps = steps;
    });

    return JSON.stringify(originalRec) != JSON.stringify(rec);
  }

}
